import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Sparkles, Plus } from "lucide-react";
import { toast } from "sonner";

/**
 * Renders a small row of "flavor pack" suggestion chips derived from the event
 * type. Clicking a chip fires `onAdd(item)` — parent handles the actual create.
 *
 * Props:
 *   eventId   – required
 *   kind      – "vendor" | "budget"
 *   onAdd     – async (item:string) => void   parent creates the row
 *   version   – any value (increment to force a refetch after adds/deletes)
 */
export default function SuggestionChips({ eventId, kind, onAdd, version = 0 }) {
  const [pack, setPack] = useState(null);
  const [busy, setBusy] = useState(null); // which item is currently being added

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/events/${eventId}/suggestions`);
      setPack(data);
    } catch (e) { console.error("suggestion chips load failed:", e); }
  }, [eventId]);

  useEffect(() => { load(); }, [load, version]);

  if (!pack || !pack.pack_id) return null;
  const items = kind === "vendor" ? pack.vendor_categories : pack.budget_lines;
  if (!items || items.length === 0) return null;

  const handle = async (it) => {
    setBusy(it);
    try { await onAdd(it); }
    catch (e) { toast.error("Could not add"); }
    finally { setBusy(null); load(); }
  };

  return (
    <div
      data-testid={`suggestions-${kind}`}
      className="bevel border-hair rounded-sm p-4"
    >
      <div className="flex items-baseline gap-2 mb-3">
        <Sparkles size={12} strokeWidth={1.5} className="text-app" />
        <div className="eyebrow">Suggested for {pack.pack_name}</div>
        <div className="text-xs text-muted font-light ml-auto">Tap to add</div>
      </div>
      <div className="flex flex-wrap gap-2">
        {items.map(it => (
          <button
            key={it}
            onClick={() => handle(it)}
            disabled={busy === it}
            data-testid={`suggest-${kind}-${it.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-hair text-xs text-app hover:border-[#0A0A0A] hover:bg-canvas disabled:opacity-50 transition-colors"
          >
            <Plus size={11} strokeWidth={1.5} className="text-muted" />
            <span>{it}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
